import { Grid2 as Grid } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import { useCallback } from 'react';
import { FormFieldsData, useFormContext } from 'framework/form';
import SendReportModal from '../sendReport/SendReportModal';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { blobToBase64 } from '../approve/ApproveCustomerReportLayout';
import { getRenderingApiUrl } from 'infrastructure/pdf';
import { useParams } from 'react-router-dom';
import useCustomerReportService from '../useCustomerReportService';
import { useCustomerReportPlaceholders } from '../hooks/useCustomerReportPlaceholders';
import { generatePdf, markdownToHtml, replacePlaceholders } from 'framework/markdownEditor';
import { ApprovalType } from 'domain/shared/newProject/customerReport/types';
import { CUSTOMER_REPORT_STATUS } from 'domain/shared/newProject';

const CustomerReportActionButtons = ({ approvalType }: { approvalType?: ApprovalType }) => {
  const requiresApproval =
    approvalType === ApprovalType.MANUAL_APPROVAL || approvalType === ApprovalType.AUTO_APPROVAL_AFTER_3_BUSINESS_DAYS;
  const { data = {}, isSubmitEnabled, submitForm } = useFormContext();
  const { id, reportId } = useParams();
  const { formatDate } = useDateService();
  const { sendSyberryReport, sendDirectReport } = useCustomerReportService();
  const { calculatePlaceholders } = useCustomerReportPlaceholders(id);

  const submit = useCallback(
    (isDraft: boolean) => {
      data.isDraft = isDraft;
      submitForm?.();
    },
    [data, submitForm],
  );

  return (
    <Grid container direction="row" spacing={1} sx={{ justifyContent: 'end', alignItems: 'center' }}>
      <Grid>
        <Button variant="outlined" disabled={!isSubmitEnabled} onClick={() => submit(true)}>
          Save as draft
        </Button>
      </Grid>
      {requiresApproval ? (
        <Grid>
          <Button disabled={!isSubmitEnabled} onClick={() => submit(false)}>
            Send for approval
          </Button>
        </Grid>
      ) : (
        <Grid>
          <SendReportModal
            projectId={id}
            contentGenerator={async () => {
              const placeholders = await calculatePlaceholders(data, data.template);

              return generatePdf(
                getRenderingApiUrl(),
                markdownToHtml(replacePlaceholders(data.template, placeholders ?? {})),
                {},
              ).then(blobToBase64);
            }}
            titleInterval={`${formatDate(data?.periodStart, DATE_FORMAT.NATURAL)} — ${formatDate(data?.periodEnd, DATE_FORMAT.NATURAL)}`}
            disabled={!data?.periodStart || !data?.periodEnd}
            onSubmit={(submitBody: FormFieldsData) =>
              data.status === CUSTOMER_REPORT_STATUS.APPROVED
                ? sendSyberryReport(reportId!, submitBody)
                : sendDirectReport(id!, {
                    ...submitBody,
                    id: reportId || null,
                    businessOutcomes: data?.businessOutcomes,
                    milestones: data?.milestones,
                    risks: data?.risks,
                    changeRequests: data?.changeRequests,
                    periodStart: data?.periodStart,
                    periodEnd: data?.periodEnd,
                    template: data?.template,
                  })
            }
          />
        </Grid>
      )}
    </Grid>
  );
};
export default CustomerReportActionButtons;
